import * as React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard, Headset, Megaphone, ListChecks, Contact, PhoneOutgoing, Users2, PhoneIncoming, Network, Radio,
  Server, Building2, Route, Ban, CalendarClock, Tags, FileText, Mic, Music, BarChart3, UserCog, Settings, ScrollText, LogOut, Moon, Sun, Menu,
  Phone, Minus, ChevronRight, Building, Undo2, ShieldCheck, Palette, Bell, Plug,
  PhoneCall, Smartphone, UsersRound, Hand, Volume2, ParkingSquare, Presentation, Voicemail, Lock, Hash, Activity, ShieldAlert, Zap,
} from 'lucide-react';
import { useAuth, hasRole, can, hasModule, type Role, type Tenant } from '@/store/auth';
import { useSwitchTenant } from '@/pages/admin/Platform';
import { usePhone } from '@/store/phone';
import { useAgentMe, useAgentState, freshRegistration } from '@/hooks/agent';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/badge';
import { Softphone } from '@/components/Softphone';
import { LiveIndicator } from '@/components/LiveIndicator';
import { api, errorText } from '@/lib/api';
import { toast } from '@/store/toast';
import { cn, enumLabel } from '@/lib/utils';
import { LanguageMenu, applyUserLanguage } from '@/components/LanguageMenu';
import { InstallButton } from '@/lib/pwa';
import { t } from '@/i18n';
import { useSessionBranding, useBranding, BrandLogo, type Branding } from '@/lib/branding';
import { useRealtimeConnection } from '@/hooks/live';
import { SlaBanner } from '@/components/SlaBanner';

const PAUSE_REASONS = ['BREAK', 'LUNCH', 'MEETING', 'TRAINING', 'TECH'];

// The admin/supervisor softphone sits behind a round phone button (bottom right) and opens only when
// that button is clicked or a call comes in. It stays mounted while closed, so a call or a consult
// in progress keeps its state; the button turns green during a call and pulses while ringing.
function FloatingSoftphone() {
  const phone = usePhone();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { if (phone.call === 'incoming') setOpen(true); }, [phone.call]);
  const ringing = phone.call === 'incoming';
  const inCall = phone.call === 'active' || phone.call === 'outgoing';
  const dot = phone.reg === 'registered' ? 'bg-success' : phone.reg === 'failed' ? 'bg-destructive' : 'bg-muted-foreground';
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      <div className={cn('w-80 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl', !open && 'hidden')} data-testid="softphone-panel">
        <div className="relative">
          <Button variant="ghost" size="icon" className="absolute right-1 top-1 z-10 h-7 w-7" aria-label={t('Minimize softphone')} onClick={() => setOpen(false)}><Minus /></Button>
          <Softphone />
        </div>
      </div>
      <Button size="icon" variant={inCall || ringing ? 'success' : 'default'} aria-label={open ? t('Hide softphone') : t('Show softphone')}
        aria-expanded={open} data-testid="softphone-toggle" onClick={() => setOpen((o) => !o)}
        className={cn('relative h-12 w-12 rounded-full shadow-lg', ringing && 'animate-pulse')}>
        <Phone className="h-5 w-5" />
        <span className={cn('absolute right-0.5 top-0.5 h-3 w-3 rounded-full border-2 border-background', dot)} />
      </Button>
    </div>
  );
}

// After a call the extension is in WRAPUP until a disposition is saved (as on the agent's Call page);
// without this an admin/supervisor would have no way out of WRAPUP from the admin portal.
function WrapupControl({ onDone }: { onDone: () => void }) {
  const [code, setCode] = React.useState('');
  const call = useQuery({ queryKey: ['agent-call', 'admin-wrapup'], queryFn: () => api<{ dispositions: { code: string; name: string }[] }>('/agent/call') });
  async function save() {
    try { await api('/agent/disposition', { body: { code } }); toast.ok(t('Disposition saved')); setCode(''); onDone(); }
    catch (e) { toast.error(errorText(e)); }
  }
  return (<>
    <Select className="h-8 w-40" value={code} onChange={(e) => setCode(e.target.value)} aria-label={t('Disposition')}>
      <option value="">{t('Disposition…')}</option>
      {(call.data?.dispositions ?? []).map((d) => <option key={d.code} value={d.code}>{d.code} · {d.name}</option>)}
    </Select>
    <Button size="sm" disabled={!code} onClick={save}>{t('Finish wrap-up')}</Button>
  </>);
}

// need: the rights (any of) that show the item (PHASE23 permission groups)
interface NavItem { to: string; label: string; icon: React.ComponentType<{ className?: string }>; roles?: Role[]; need?: string[] }
interface NavGroup { group: string; icon: React.ComponentType<{ className?: string }>; items: NavItem[] }
// The main menu: Dashboard, then a few submenus that open on click (one at a time; the one holding
// the current page opens by itself), so the top level stays short.
const HOME: NavItem = { to: '/admin', label: 'Dashboard', icon: LayoutDashboard };
const NAV: NavGroup[] = [
  { group: 'Reports', icon: BarChart3, items: [
    { to: '/admin/reports', label: 'Reports', icon: BarChart3, need: ['reports.view'] },
    { to: '/admin/cdr', label: 'CDR', icon: FileText, need: ['cdr.view'] },
    { to: '/admin/recordings', label: 'Recordings', icon: Mic, need: ['recordings.view'] },
    { to: '/admin/audio-library', label: 'Audio Library', icon: Music, need: ['audio.view'] },
  ] },
  { group: 'Outbound', icon: PhoneOutgoing, items: [
    { to: '/admin/campaigns', label: 'Campaigns', icon: Megaphone, need: ['campaigns.view'] },
    { to: '/admin/dialer', label: 'Dialer', icon: PhoneOutgoing, need: ['campaigns.view'] },
    { to: '/admin/contact-lists', label: 'Contact Lists', icon: ListChecks, need: ['contacts.view'] },
    { to: '/admin/contacts', label: 'Contacts', icon: Contact, need: ['contacts.view'] },
    { to: '/admin/callbacks', label: 'Callbacks', icon: CalendarClock, need: ['callbacks.view'] },
    { to: '/admin/dispositions', label: 'Dispositions', icon: Tags, need: ['dispositions.view'] },
    { to: '/admin/dnc', label: 'DNC', icon: Ban, need: ['dnc.view'] },
    { to: '/admin/broadcast', label: 'Broadcast', icon: Radio, need: ['broadcasts.view'] },
  ] },
  { group: 'Inbound', icon: PhoneIncoming, items: [
    { to: '/admin/agents', label: 'Agents', icon: Headset, need: ['agents.view'] },
    { to: '/admin/queues', label: 'Queues', icon: Users2, need: ['queues.view'] },
    { to: '/admin/inbound', label: 'Inbound', icon: PhoneIncoming, need: ['inbound.view'] },
    { to: '/admin/ivr', label: 'IVR', icon: Network, need: ['ivr.view'] },
  ] },
  // IP PBX (docs/PBX.md): shown only to tenants with the PBX module (can() checks the section's module)
  { group: 'PBX', icon: PhoneCall, items: [
    { to: '/admin/pbx', label: 'PBX status', icon: Activity, need: ['extensions.view'] },
    { to: '/admin/extensions', label: 'Extensions', icon: PhoneCall, need: ['extensions.view'] },
    { to: '/admin/phones', label: 'Phones', icon: Smartphone, need: ['extensions.view'] },
    { to: '/admin/ring-groups', label: 'Ring groups', icon: UsersRound, need: ['pbx_groups.view'] },
    { to: '/admin/pickup-groups', label: 'Pickup groups', icon: Hand, need: ['pbx_groups.view'] },
    { to: '/admin/paging-groups', label: 'Paging groups', icon: Volume2, need: ['pbx_groups.view'] },
    { to: '/admin/park', label: 'Call park', icon: ParkingSquare, need: ['pbx_groups.view'] },
    { to: '/admin/conferences', label: 'Conference rooms', icon: Presentation, need: ['conferences.view'] },
    { to: '/admin/voicemail-boxes', label: 'Voicemail boxes', icon: Voicemail, need: ['voicemail_boxes.view'] },
    { to: '/admin/class-of-service', label: 'Class of service', icon: Lock, need: ['pbx_settings.view'] },
    { to: '/admin/feature-codes', label: 'Feature codes', icon: Hash, need: ['pbx_settings.view'] },
    { to: '/admin/speed-dial', label: 'Speed dial', icon: Zap, need: ['pbx_settings.view'] },
  ] },
  { group: 'Carriers', icon: Server, items: [
    { to: '/admin/trunks', label: 'SIP Trunks', icon: Server, need: ['routing.view'] },
    { to: '/admin/inbound-trunks', label: 'Inbound trunks', icon: PhoneIncoming, need: ['routing.view'] },
    { to: '/admin/carriers', label: 'Carriers', icon: Building2, need: ['routing.view'] },
    { to: '/admin/routing', label: 'Routing', icon: Route, need: ['routing.view'] },
  ] },
  { group: 'Admin', icon: Settings, items: [
    { to: '/admin/users', label: 'Users', icon: UserCog, need: ['users.view'] },
    { to: '/admin/permission-groups', label: 'Permission groups', icon: ShieldCheck, need: ['permission_groups.view'] },
    { to: '/admin/branding', label: 'Branding', icon: Palette, need: ['branding.view'] },
    { to: '/admin/notifications', label: 'Notifications', icon: Bell, need: ['notifications.view'] },
    { to: '/admin/crm', label: 'CRM', icon: Plug, need: ['crm.view'] },
    { to: '/admin/settings', label: 'Settings', icon: Settings, need: ['settings.view'] },
    { to: '/admin/audit-logs', label: 'Audit Logs', icon: ScrollText, need: ['audit.view'] },
  ] },
  { group: 'Platform', icon: Building, items: [
    { to: '/admin/tenants', label: 'Tenants', icon: Building, roles: ['PLATFORM'] },
    { to: '/admin/sip-security', label: 'SIP security', icon: ShieldAlert, roles: ['PLATFORM'] },
  ] },
];

// the first page this user may open (the dashboard when allowed)
export function firstPage(auth: Parameters<typeof can>[0]): string | null {
  if (can(auth, 'dashboard.view') && hasModule(auth.tenant, 'CALLCENTER')) return '/admin';
  for (const g of NAV) for (const i of g.items) if ((!i.roles || hasRole(auth.user?.role, i.roles)) && (!i.need || can(auth, ...i.need))) return i.to;
  return null;
}

const LABEL = 'md:hidden md:group-hover:inline md:group-focus-within:inline';
const itemClass = ({ isActive }: { isActive: boolean }) => cn('flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-sm hover:bg-accent', isActive && 'bg-accent font-medium');

function MainMenu({ role, onPick }: { role: Role | undefined; onPick: () => void }) {
  const { pathname } = useLocation();
  const auth = useAuth();
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => (!i.roles || hasRole(role, i.roles)) && (!i.need || can(auth, ...i.need))) }))
    .filter((g) => g.items.length);
  const showHome = can(auth, 'dashboard.view') || (hasModule(auth.tenant, 'PBX') && !hasModule(auth.tenant, 'CALLCENTER') && can(auth, 'extensions.view'));
  const current = groups.find((g) => g.items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`)))?.group ?? null;
  const [open, setOpen] = React.useState<string | null>(current);
  React.useEffect(() => { if (current) setOpen(current); }, [current]);
  return (
    <nav className="flex flex-col gap-1" aria-label={t('Main')}>
      {showHome && <NavLink to={HOME.to} end onClick={onPick} title={t(HOME.label)} className={itemClass}>
        <HOME.icon className="h-4 w-4 shrink-0" /> <span className={LABEL}>{t(HOME.label)}</span>
      </NavLink>}
      {auth.extension && hasModule(auth.tenant, 'PBX') && <NavLink to="/admin/my-phone" onClick={onPick} title={t('My phone')} className={itemClass} data-testid="menu-my-phone">
        <Phone className="h-4 w-4 shrink-0" /> <span className={LABEL}>{t('My phone')}</span>
      </NavLink>}
      {auth.user?.role === 'AGENT' && <NavLink to="/agent/dashboard" onClick={onPick} title={t('Agent panel')} className={itemClass} data-testid="menu-agent-panel">
        <Headset className="h-4 w-4 shrink-0" /> <span className={LABEL}>{t('Agent panel')}</span>
      </NavLink>}
      {groups.map((g) => {
        const expanded = open === g.group;
        return (
          <div key={g.group}>
            <button type="button" aria-expanded={expanded} title={t(g.group)} data-testid={`menu-${g.group.toLowerCase()}`}
              onClick={() => setOpen(expanded ? null : g.group)}
              className={cn('flex w-full items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-sm hover:bg-accent', current === g.group && 'font-medium text-primary')}>
              <g.icon className="h-4 w-4 shrink-0" /> <span className={cn('flex-1 text-left', LABEL)}>{t(g.group)}</span>
              <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', LABEL, expanded && 'rotate-90')} />
            </button>
            {/* in the icon rail (desktop, menu not opened) only the submenu names show */}
            {expanded && (
              <div className="ml-3 flex flex-col border-l pl-2 md:hidden md:group-hover:flex md:group-focus-within:flex">
                {g.items.map((i) => (
                  <NavLink key={i.to} to={i.to} onClick={onPick} title={t(i.label)} className={itemClass}>
                    <i.icon className="h-4 w-4 shrink-0" /> <span>{t(i.label)}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function useTheme() {
  const [dark, setDark] = React.useState(() => localStorage.getItem('cc-theme') === 'dark');
  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('cc-theme', dark ? 'dark' : 'light');
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

// the tenant this session works in; a platform admin working inside another tenant sees a bar to go back
// (and the user's rights: menus and buttons show only what the API allows)
function useSessionTenant() {
  const { user, tenant, homeTenantId, setTenant, setPermissions, setExtension } = useAuth();
  const setBranding = useSessionBranding((s) => s.set);
  const me = useQuery({ queryKey: ['me', user?.id, user?.tenant_id],
                        queryFn: () => api<{ tenant: Tenant | null; home_tenant_id: number; permissions: string[]; branding?: Branding; language?: string;
                                                         extension?: { id: number; number: string } | null }>('/auth/me') });
  React.useEffect(() => {
    if (me.data) {
      setTenant(me.data.tenant, me.data.home_tenant_id); setPermissions(me.data.permissions ?? null); setExtension(me.data.extension ?? null);
      if (me.data.branding) setBranding(me.data.branding);
      applyUserLanguage(me.data);
    }
  }, [me.data, setTenant, setPermissions, setBranding, setExtension]);
  return { tenant, away: !!tenant && homeTenantId !== null && tenant.id !== homeTenantId, homeTenantId };
}

// a platform admin picks the tenant to work in from the top bar (2026-10-13); the same switch as
// Platform → Tenants → Work in this tenant. Only ACTIVE tenants and the current one are listed.
function TenantSwitcher({ current, onSwitch }: { current: Tenant | null; onSwitch: (id: number) => void }) {
  const q = useQuery({ queryKey: ['tenants', 'switcher'], staleTime: 60_000,
                       queryFn: () => api<{ rows: (Tenant & { status: string })[] }>('/platform/tenants') });
  const rows = (q.data?.rows ?? []).filter((r) => r.status === 'ACTIVE' || r.id === current?.id);
  return (
    <label className="flex items-center gap-1.5 text-sm" title={t('Switch tenant')}>
      <Building className="h-4 w-4 text-muted-foreground" />
      <Select className="h-8 max-w-48" aria-label={t('Switch tenant')} data-testid="tenant-switcher" value={current?.id ?? ''}
        onChange={(e) => { const id = Number(e.target.value); if (id && id !== current?.id) onSwitch(id); }}>
        {!rows.some((r) => r.id === current?.id) && current && <option value={current.id}>{current.name}</option>}
        {rows.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.sip_domain})</option>)}
      </Select>
    </label>
  );
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const session = useSessionTenant();
  const brand = useBranding();
  const switchTenant = useSwitchTenant();
  // a single-company install (only the default tenant) shows no tenant name
  const showTenant = user?.role === 'PLATFORM' || (session.tenant?.id ?? 1) !== 1;
  const nav = useNavigate();
  const theme = useTheme();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('BREAK');
  useRealtimeConnection();

  // an admin/supervisor with their own extension (agents row linked via users.agent_id) gets a
  // softphone just like an agent -- same hooks, same engine commands, reachable for barging too.
  const hasExtension = !!user?.agent_id;
  const me = useAgentMe(hasExtension);
  const st = useAgentState(hasExtension);
  const phone = usePhone();
  const state = st.data?.agent.state ?? me.data?.agent.state ?? '...';

  React.useEffect(() => {
    if (hasExtension && me.data && phone.reg === 'disconnected') {
      (me.data.agent.state === 'LOGGED_OUT' ? freshRegistration(me.data.sip) : phone.connect(me.data.sip)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExtension, me.data]);

  async function act(path: string, body?: unknown) {
    try { await api(`/agent/${path}`, { body: body ?? {} }); await qc.invalidateQueries({ queryKey: ['agent-state'] }); }
    catch (e) { toast.error(errorText(e)); }
  }

  // login and logout start from a clean SIP registration: every old one is dropped, one new REGISTER
  const [regBusy, setRegBusy] = React.useState(false);
  async function login() {
    setRegBusy(true);
    try {
      if (!await freshRegistration(me.data?.sip)) toast.error(t('Softphone did not register again; logging in anyway'));
      await act('login', { initial: 'PAUSED' });
    } finally { setRegBusy(false); }
  }
  async function logoutAgent() {
    setRegBusy(true);
    try { await act('logout'); await freshRegistration(me.data?.sip); await qc.invalidateQueries({ queryKey: ['agent-state'] }); }
    finally { setRegBusy(false); }
  }

  const registered = phone.reg === 'registered';
  return (
    <div className="flex min-h-screen">
      {/* Menu: on desktop a rail of icons that opens over the page on hover (no layout shift); on
          small screens the Menu button slides the full menu in, as before */}
      <div className="hidden w-14 shrink-0 md:block" aria-hidden />
      <aside data-testid="sidebar"
        className={cn('group fixed inset-y-0 left-0 z-40 w-60 overflow-y-auto overflow-x-hidden border-r bg-sidebar p-3 transition-[width,transform] duration-200 md:w-14 md:translate-x-0 md:p-2 md:hover:w-60 md:hover:p-3 md:hover:shadow-xl md:focus-within:w-60 md:focus-within:p-3',
          open ? 'translate-x-0' : '-translate-x-full')}>
        <div className="mb-4 flex items-center gap-2 whitespace-nowrap px-2 pt-1 text-lg font-semibold"><BrandLogo className="h-5 w-5 shrink-0" /> <span className="md:hidden md:group-hover:inline md:group-focus-within:inline" data-testid="sidebar-brand">{brand.product_name}</span></div>
        <MainMenu role={user?.role} onPick={() => setOpen(false)} />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex min-h-14 flex-wrap items-center gap-2 border-b bg-background/95 px-3 py-2 backdrop-blur sm:gap-3 sm:px-4">
          <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('Menu')} onClick={() => setOpen((o) => !o)}><Menu /></Button>
          <div className="flex-1" />
          {hasExtension && (
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-extension">
              <span data-testid="agent-state"><StatusBadge value={state} /></span>
              {state === 'LOGGED_OUT' && <Button size="sm" disabled={regBusy} onClick={login}>{t('Log in')}</Button>}
              {state === 'WRAPUP' && <WrapupControl onDone={() => qc.invalidateQueries({ queryKey: ['agent-state'] })} />}
              {(state === 'PAUSED' || state === 'DISCONNECTED') && (
                <Button size="sm" variant="success" disabled={!registered} title={registered ? '' : t('Your softphone must be registered first')} onClick={() => act('ready')}>{t('Ready')}</Button>
              )}
              {state === 'READY' && (<>
                <Select className="h-8 w-32" value={reason} onChange={(e) => setReason(e.target.value)} aria-label={t('Pause reason')}>{PAUSE_REASONS.map((r) => <option key={r} value={r}>{enumLabel(r)}</option>)}</Select>
                <Button size="sm" variant="warning" onClick={() => act('pause', { reason })}>{t('Pause')}</Button>
              </>)}
              {(state === 'PAUSED' || state === 'READY' || state === 'DISCONNECTED') && <Button size="sm" variant="outline" disabled={regBusy} onClick={logoutAgent}>{t('Log out')}</Button>}
            </div>
          )}
          <LiveIndicator />
          <InstallButton compact />
          <LanguageMenu />
          <Button variant="ghost" size="icon" aria-label={t('Toggle theme')} onClick={theme.toggle}>{theme.dark ? <Sun /> : <Moon />}</Button>
          {user?.role === 'PLATFORM' && <TenantSwitcher current={session.tenant} onSwitch={switchTenant} />}
          <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="whoami">
            {user?.name} · {user ? enumLabel(user.role).toLowerCase() : ''}{showTenant && user?.role !== 'PLATFORM' && session.tenant ? ` · ${session.tenant.name}` : ''}
          </span>
          <Button variant="outline" size="sm" onClick={async () => {
            if (hasExtension) {
              if (state !== 'LOGGED_OUT' && state !== '...') await api('/agent/logout', { body: {} }).catch(() => {});
              await freshRegistration(me.data?.sip, false).catch(() => {});
              await phone.disconnect().catch(() => {});
            }
            logout(); nav('/login');
          }} aria-label={t('Sign out')}><LogOut /> <span className="hidden sm:inline">{t('Sign out')}</span></Button>
        </header>
        {session.away && (
          <div className="flex items-center gap-3 border-b bg-warning/15 px-4 py-2 text-sm" data-testid="tenant-banner">
            <Building className="h-4 w-4" />
            <span>{(() => {
              // the tenant name stays bold: split the translated sentence around it
              const [before, after] = t('Working inside {{name}} ({{slug}}). Changes here are made for that tenant.', { name: '\u0000', slug: session.tenant?.slug ?? '' }).split('\u0000');
              return <>{before}<b>{session.tenant?.name}</b>{after}</>;
            })()}</span>
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => session.homeTenantId && switchTenant(session.homeTenantId)}><Undo2 /> {t('Back to my tenant')}</Button>
          </div>
        )}
        <SlaBanner />
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
        {hasExtension && <FloatingSoftphone />}
      </div>
    </div>
  );
}
